import { z } from "zod";
import { IsoDateTime, PageQuery, Uuid } from "./common";
import { MESSAGING_LANGUAGES } from "./messaging";
import { CreativeSurface } from "./studio";

// ---------------------------------------------------------------------------
// Vocabularies
// ---------------------------------------------------------------------------

export const TOPIC_STATUSES = ["in_product", "to_check", "not_yet", "tip"] as const;
export const TOPIC_CHANNELS = ["post", "ad", "both"] as const;
export const TOPIC_PRIORITIES = ["high", "normal", "low", "no"] as const;
export const TOPIC_SOURCES = ["import", "manual", "generated", "weekly", "news", "calendar", "competitor"] as const;
export const TOPIC_LIFECYCLES = ["proposed", "active", "archived", "merged"] as const;
export const TOPIC_SORTS = ["priority", "code", "score", "newest", "last_used"] as const;
export const REASON_SOURCES = ["fact", "sales", "calendar", "season", "memory", "competitor", "news", "bank", "brand", "rhythm"] as const;
export const THEME_STATUSES = ["proposed", "chosen", "rejected", "done"] as const;
export const CALENDAR_KINDS = ["holiday", "international", "season", "deadline", "commercial", "fasting", "local"] as const;
export const WEEK_PLAN_STATUSES = ["proposed", "approved", "rejected", "generating", "generated", "done"] as const;
export const PLAN_ITEM_KINDS = ["post", "ad", "email"] as const;
export const PLAN_ITEM_SLOTS = ["high", "exploration", "pinned", "news", "ad", "email"] as const;
export const NEWS_STATUSES = ["active", "used", "retired"] as const;
export const NEWS_SOURCES = ["feed", "manual"] as const;
export const TOPIC_RUN_KINDS = ["themes", "topics", "round", "plan"] as const;
export const TOPIC_RUN_STATUSES = ["running", "ok", "partial", "failed"] as const;
export const GENERATION_SOURCES = ["facts", "sales", "calendar", "memory", "competitors", "news"] as const;
export const MAX_ROUND_TOPICS = 10;
export const MAX_COMPETITOR_PAGES = 10;
export const MAX_COMPETITOR_TERMS = 5;

export const TopicStatus = z.enum(TOPIC_STATUSES);
export type TopicStatus = z.infer<typeof TopicStatus>;
export const TopicChannel = z.enum(TOPIC_CHANNELS);
export type TopicChannel = z.infer<typeof TopicChannel>;
export const TopicPriority = z.enum(TOPIC_PRIORITIES);
export type TopicPriority = z.infer<typeof TopicPriority>;
export const TopicSource = z.enum(TOPIC_SOURCES);
export type TopicSource = z.infer<typeof TopicSource>;
export const TopicLifecycle = z.enum(TOPIC_LIFECYCLES);
export type TopicLifecycle = z.infer<typeof TopicLifecycle>;
export const ReasonSource = z.enum(REASON_SOURCES);
export type ReasonSource = z.infer<typeof ReasonSource>;
export const WeekPlanStatus = z.enum(WEEK_PLAN_STATUSES);
export type WeekPlanStatus = z.infer<typeof WeekPlanStatus>;
export const PlanItemKind = z.enum(PLAN_ITEM_KINDS);
export type PlanItemKind = z.infer<typeof PlanItemKind>;
export const CalendarKind = z.enum(CALENDAR_KINDS);
export type CalendarKind = z.infer<typeof CalendarKind>;
export const GenerationSource = z.enum(GENERATION_SOURCES);
export type GenerationSource = z.infer<typeof GenerationSource>;

const LocalDate = z.string().regex(/^\d{4}-\d{2}-\d{2}$/u, "expected YYYY-MM-DD");
const Month = z.string().regex(/^\d{4}-(0[1-9]|1[0-2])$/u, "expected YYYY-MM");
const Title = z.string().trim().min(1).max(200);
const LongText = z.string().trim().max(2_000);
const Note = z.string().trim().max(1_000);
const ArchetypeId = z.string().trim().min(1).max(80);

/** One line of an explanation: `ref` names an input of the planning context, so "why" can always be traced back. */
export const ReasonDto = z.object({ ref: z.string(), source: ReasonSource, text: z.string() });
export type ReasonDto = z.infer<typeof ReasonDto>;

export const DraftFactDto = z.object({
  text: z.string(),
  category: z.string(),
  kind: z.string().nullable(),
  /** Stated in a draft only: it stays out of every generated text until a person approves it as a fact. */
  numbers: z.array(z.string()),
});
export type DraftFactDto = z.infer<typeof DraftFactDto>;

// ---------------------------------------------------------------------------
// Topics
// ---------------------------------------------------------------------------

export const TopicDto = z.object({
  id: Uuid,
  code: z.string(),
  categoryId: Uuid.nullable(),
  categoryName: z.string().nullable(),
  title: z.string(),
  description: z.string().nullable(),
  status: TopicStatus,
  channel: TopicChannel,
  priority: TopicPriority,
  pinnedWeek: LocalDate.nullable(),
  source: TopicSource,
  sourceRef: z.string().nullable(),
  sourceNote: z.string().nullable(),
  lifecycle: TopicLifecycle,
  archetype: z.string().nullable(),
  profileId: Uuid.nullable(),
  angleId: Uuid.nullable(),
  factIds: z.array(Uuid),
  note: z.string().nullable(),
  preview: z.string().nullable(),
  why: z.array(ReasonDto),
  draftFacts: z.array(DraftFactDto),
  themeId: Uuid.nullable(),
  mergedIntoId: Uuid.nullable(),
  score: z.number().nullable(),
  scoreReach: z.number().int(),
  scoreClicks: z.number().int(),
  scoreEngagement: z.number().int(),
  scoreLeads: z.number().int(),
  scoreOrders: z.number().int(),
  scoredAt: IsoDateTime.nullable(),
  /** Why it cannot go out in a post now: status, priority, channel, state, exclusion. Empty: it can. */
  postBlockers: z.array(z.string()),
  /** Why it cannot run as an ad now. The hard rule: only in_product, with an approved fact. */
  adBlockers: z.array(z.string()),
  createdAt: IsoDateTime,
  updatedAt: IsoDateTime,
});
export type TopicDto = z.infer<typeof TopicDto>;

const TopicEditable = {
  title: Title,
  description: LongText.nullable(),
  categoryId: Uuid.nullable(),
  status: TopicStatus,
  channel: TopicChannel,
  priority: TopicPriority,
  pinnedWeek: LocalDate.nullable(),
  archetype: ArchetypeId.nullable(),
  profileId: Uuid.nullable(),
  angleId: Uuid.nullable(),
  factIds: z.array(Uuid).max(20),
  note: Note.nullable(),
};

export const TopicCreate = z
  .object({
    title: Title,
    description: LongText.nullable().optional(),
    categoryId: Uuid.nullable().optional(),
    status: TopicStatus.default("to_check"),
    channel: TopicChannel.default("post"),
    priority: TopicPriority.default("normal"),
    pinnedWeek: LocalDate.nullable().optional(),
    archetype: ArchetypeId.nullable().optional(),
    profileId: Uuid.nullable().optional(),
    angleId: Uuid.nullable().optional(),
    factIds: z.array(Uuid).max(20).default([]),
    note: Note.nullable().optional(),
  })
  .strict();
export type TopicCreate = z.input<typeof TopicCreate>;

export const TopicPatch = z
  .object({ ...TopicEditable, lifecycle: z.enum(["active", "archived"]) })
  .partial()
  .strict();
export type TopicPatch = z.infer<typeof TopicPatch>;

export const TopicListQuery = PageQuery.extend({
  categoryId: Uuid.optional(),
  status: TopicStatus.optional(),
  channel: TopicChannel.optional(),
  priority: TopicPriority.optional(),
  source: TopicSource.optional(),
  lifecycle: TopicLifecycle.optional(),
  pinnedWeek: LocalDate.optional(),
  themeId: Uuid.optional(),
  q: z.string().trim().min(1).max(200).optional(),
  sort: z.enum(TOPIC_SORTS).default("priority"),
});
export type TopicListQuery = z.infer<typeof TopicListQuery>;

export const TopicBulkAction = z.discriminatedUnion("action", [
  z.object({ action: z.literal("set_priority"), priority: TopicPriority }),
  z.object({ action: z.literal("set_channel"), channel: TopicChannel }),
  z.object({ action: z.literal("set_status"), status: TopicStatus }),
  z.object({ action: z.literal("set_category"), categoryId: Uuid.nullable() }),
  z.object({ action: z.literal("activate") }),
  z.object({ action: z.literal("archive") }),
  z.object({ action: z.literal("do_not_promote") }),
]);
export const TopicBulkRequest = z.object({ ids: z.array(Uuid).min(1).max(500), change: TopicBulkAction });
export type TopicBulkRequest = z.infer<typeof TopicBulkRequest>;
export const TopicBulkResponse = z.object({
  changed: z.array(Uuid),
  skipped: z.array(z.object({ id: Uuid, reason: z.string() })),
});
export type TopicBulkResponse = z.infer<typeof TopicBulkResponse>;

export const TopicMergeRequest = z.object({ sourceIds: z.array(Uuid).min(1).max(20), targetId: Uuid });
export type TopicMergeRequest = z.infer<typeof TopicMergeRequest>;

export const SimilarTopicDto = z.object({ id: Uuid, code: z.string(), title: z.string(), lifecycle: TopicLifecycle, similarity: z.number() });
export type SimilarTopicDto = z.infer<typeof SimilarTopicDto>;
export const SimilarTopicsResponse = z.object({ items: z.array(SimilarTopicDto) });

export const TopicHistoryEntry = z.object({
  at: IsoDateTime,
  action: z.string(),
  actorName: z.string().nullable(),
  before: z.unknown().nullable(),
  after: z.unknown().nullable(),
});
export const TopicHistoryResponse = z.object({ items: z.array(TopicHistoryEntry), usages: z.array(z.lazy(() => TopicUsageDto)) });

export const TopicCategoryDto = z.object({
  id: Uuid,
  code: z.string(),
  name: z.string(),
  intro: z.string().nullable(),
  sort: z.number().int(),
  status: z.enum(["active", "archived"]),
  topicCount: z.number().int(),
});
export type TopicCategoryDto = z.infer<typeof TopicCategoryDto>;
export const TopicCategoryInput = z
  .object({
    code: z.string().regex(/^[A-Z]{1,3}$/u).optional(),
    name: z.string().trim().min(1).max(120),
    intro: z.string().trim().max(500).nullable().optional(),
    sort: z.number().int().min(0).max(10_000).optional(),
  })
  .strict();
export const TopicCategoryPatch = TopicCategoryInput.omit({ code: true }).extend({ status: z.enum(["active", "archived"]) }).partial().strict();

export const TopicExclusionDto = z.object({
  id: Uuid,
  text: z.string(),
  note: z.string().nullable(),
  status: z.enum(["active", "archived"]),
  createdAt: IsoDateTime,
});
export type TopicExclusionDto = z.infer<typeof TopicExclusionDto>;
export const TopicExclusionInput = z.object({ text: z.string().trim().min(2).max(300), note: Note.nullable().optional() }).strict();
export const TopicExclusionPatch = z.object({ note: Note.nullable(), status: z.enum(["active", "archived"]) }).partial().strict();

export const ImportSeedResponse = z.object({
  seed: z.string().nullable(),
  categoriesAdded: z.number().int(),
  topicsAdded: z.number().int(),
  exclusionsAdded: z.number().int(),
});
export type ImportSeedResponse = z.infer<typeof ImportSeedResponse>;

export const GenerateTopicsRequest = z
  .object({
    sources: z.array(GenerationSource).min(1).default([...GENERATION_SOURCES]),
    count: z.number().int().min(1).max(30).default(10),
    brief: z.string().trim().max(1_000).optional(),
    from: LocalDate.optional(),
    to: LocalDate.optional(),
  })
  .strict();
export type GenerateTopicsRequest = z.input<typeof GenerateTopicsRequest>;

export const DroppedProposal = z.object({ title: z.string(), violations: z.array(z.string()) });
export type DroppedProposal = z.infer<typeof DroppedProposal>;

export const GenerateTopicsResponse = z.object({
  runId: Uuid,
  status: z.enum(TOPIC_RUN_STATUSES),
  items: z.array(TopicDto),
  dropped: z.array(DroppedProposal),
  repaired: z.number().int(),
});
export type GenerateTopicsResponse = z.infer<typeof GenerateTopicsResponse>;

/** Ids to keep from a generation's preview: they become active, the others of the run's list are archived. */
export const KeepTopicsRequest = z.object({ keep: z.array(Uuid).max(100), discard: z.array(Uuid).max(100).default([]) });

export const PromoteDraftFactRequest = z.object({ index: z.number().int().min(0) });
export const PromoteDraftFactResponse = z.object({ factId: Uuid, topic: TopicDto });

// ---------------------------------------------------------------------------
// Themes
// ---------------------------------------------------------------------------

export const ThemeIdeaDto = z.object({
  bankTopicId: Uuid.nullable(),
  title: z.string(),
  description: z.string().nullable(),
  archetype: z.string().nullable(),
  channel: TopicChannel,
  factIds: z.array(Uuid),
  preview: z.string().nullable(),
  why: z.array(ReasonDto),
});
export type ThemeIdeaDto = z.infer<typeof ThemeIdeaDto>;

export const ThemeDto = z.object({
  id: Uuid,
  month: Month,
  status: z.enum(THEME_STATUSES),
  title: z.string(),
  promise: z.string(),
  periodFrom: LocalDate,
  periodTo: LocalDate,
  channels: z.array(z.string()),
  offer: z.object({ text: z.string(), factIds: z.array(Uuid) }).nullable(),
  baseVisual: z.object({ template: z.string(), subject: z.string(), factIds: z.array(Uuid) }),
  ideas: z.array(ThemeIdeaDto),
  why: z.array(ReasonDto),
  score: z.number().nullable(),
  topicIds: z.array(Uuid),
  chosenAt: IsoDateTime.nullable(),
  chosenBy: z.string().nullable(),
  createdAt: IsoDateTime,
});
export type ThemeDto = z.infer<typeof ThemeDto>;

export const ThemeListQuery = PageQuery.extend({ month: Month.optional(), status: z.enum(THEME_STATUSES).optional() });
export type ThemeListQuery = z.infer<typeof ThemeListQuery>;

export const GenerateThemesRequest = z.object({ month: Month.optional(), brief: z.string().trim().max(1_000).optional() }).strict();
export type GenerateThemesRequest = z.input<typeof GenerateThemesRequest>;
export const GenerateThemesResponse = z.object({
  runId: Uuid,
  status: z.enum(TOPIC_RUN_STATUSES),
  items: z.array(ThemeDto),
  dropped: z.array(DroppedProposal),
  repaired: z.number().int(),
});
export type GenerateThemesResponse = z.infer<typeof GenerateThemesResponse>;

export const ThemePatch = z
  .object({
    title: Title,
    promise: z.string().trim().min(1).max(500),
    periodFrom: LocalDate,
    periodTo: LocalDate,
    channels: z.array(z.string().min(1).max(40)).min(1).max(10),
  })
  .partial()
  .strict();
export type ThemePatch = z.infer<typeof ThemePatch>;
export const ChooseThemeResponse = z.object({ theme: ThemeDto, topics: z.array(TopicDto) });

// ---------------------------------------------------------------------------
// Calendar
// ---------------------------------------------------------------------------

export const CalendarOccurrenceDto = z.object({
  /** Stable within a year: `<event key>:<start date>`. */
  ref: z.string(),
  key: z.string(),
  /** The tenant's own row, for its events and its edits. */
  eventId: Uuid.nullable(),
  name: z.string(),
  kind: CalendarKind,
  start: LocalDate,
  end: LocalDate,
  prepDays: z.number().int(),
  /** The day preparation should start. */
  prepStart: LocalDate,
  archetypes: z.array(z.string()),
  isPublicHoliday: z.boolean(),
  hidden: z.boolean(),
  note: z.string().nullable(),
  origin: z.enum(["computed", "edited", "tenant"]),
});
export type CalendarOccurrenceDto = z.infer<typeof CalendarOccurrenceDto>;

export const CalendarQuery = z.object({
  from: LocalDate,
  to: LocalDate,
  includeHidden: z.enum(["true", "false"]).optional(),
});
export const CalendarResponse = z.object({ items: z.array(CalendarOccurrenceDto) });

export const CalendarEventInput = z
  .object({
    name: z.string().trim().min(1).max(160),
    kind: CalendarKind.default("local"),
    start: LocalDate,
    end: LocalDate.optional(),
    yearly: z.boolean().default(false),
    prepDays: z.number().int().min(0).max(120).default(7),
    archetypes: z.array(ArchetypeId).max(20).default([]),
    note: Note.nullable().optional(),
  })
  .strict();
export type CalendarEventInput = z.input<typeof CalendarEventInput>;

/** An edit of a computed event: the date stays computed, the rest is the tenant's. */
export const CalendarOverrideInput = z
  .object({
    name: z.string().trim().min(1).max(160).nullable(),
    prepDays: z.number().int().min(0).max(120).nullable(),
    archetypes: z.array(ArchetypeId).max(20),
    note: Note.nullable(),
    hidden: z.boolean(),
  })
  .partial()
  .strict();
export type CalendarOverrideInput = z.infer<typeof CalendarOverrideInput>;
export const CalendarEventPatch = z
  .object({
    name: z.string().trim().min(1).max(160),
    kind: CalendarKind,
    start: LocalDate,
    end: LocalDate.nullable(),
    yearly: z.boolean(),
    prepDays: z.number().int().min(0).max(120),
    archetypes: z.array(ArchetypeId).max(20),
    note: Note.nullable(),
    hidden: z.boolean(),
  })
  .partial()
  .strict();
export type CalendarEventPatch = z.infer<typeof CalendarEventPatch>;

// ---------------------------------------------------------------------------
// Memory
// ---------------------------------------------------------------------------

export const TopicUsageDto = z.object({
  id: Uuid,
  topicId: Uuid,
  archetype: z.string().nullable(),
  kind: PlanItemKind,
  channel: z.string(),
  usedAt: IsoDateTime,
  weekStart: LocalDate,
  planId: Uuid.nullable(),
  creativeId: z.string().nullable(),
  results: z.object({
    reach: z.number().int(),
    impressions: z.number().int(),
    engagements: z.number().int(),
    clicks: z.number().int(),
    leads: z.number().int(),
    orders: z.number().int(),
  }),
});
export type TopicUsageDto = z.infer<typeof TopicUsageDto>;

export const TopicUsageInput = z
  .object({
    kind: PlanItemKind.default("post"),
    channel: z.string().trim().min(1).max(40),
    usedAt: IsoDateTime.optional(),
    creativeId: z.string().max(100).optional(),
  })
  .strict();

const Count = z.number().int().min(0);
export const TopicResultInput = z
  .object({
    usageId: Uuid,
    source: z.string().trim().min(1).max(40),
    measuredAt: IsoDateTime.optional(),
    reach: Count.default(0),
    impressions: Count.default(0),
    engagements: Count.default(0),
    clicks: Count.default(0),
    leads: Count.default(0),
    orders: Count.default(0),
  })
  .strict();
export type TopicResultInput = z.input<typeof TopicResultInput>;

export const TopicHookDto = z.object({
  id: Uuid,
  text: z.string(),
  kind: PlanItemKind,
  archetype: z.string().nullable(),
  topicId: Uuid.nullable(),
  impressions: z.number().int(),
  clicks: z.number().int(),
  leads: z.number().int(),
  score: z.number().nullable(),
  /** The language a person wrote it in. Null when it only came from measured results: those aren't offered to the writer. */
  language: z.string().nullable(),
  /** A person took it out: off the results and away from the writer, until someone brings it back. */
  retired: z.boolean(),
});
export type TopicHookDto = z.infer<typeof TopicHookDto>;
export const TopicHookInput = z
  .object({
    text: z.string().trim().min(1).max(500),
    kind: PlanItemKind.default("post"),
    archetype: ArchetypeId.nullable().optional(),
    topicId: Uuid.nullable().optional(),
    /** Sent when a person writes the line: it then reaches the writer, and a retired one comes back. Measured results leave it out. */
    language: z.enum(MESSAGING_LANGUAGES).optional(),
    /** Only what is sent changes, so a person writing a line wipes no results. */
    impressions: Count.optional(),
    clicks: Count.optional(),
    leads: Count.optional(),
  })
  .strict();
export type TopicHookInput = z.input<typeof TopicHookInput>;
/** The words can change only while nothing was measured on them: results belong to the words they were measured on. */
export const TopicHookPatch = z
  .object({ text: z.string().trim().min(1).max(500), language: z.enum(MESSAGING_LANGUAGES), retired: z.boolean() })
  .partial()
  .strict();
export type TopicHookPatch = z.input<typeof TopicHookPatch>;

// ---------------------------------------------------------------------------
// Week plan
// ---------------------------------------------------------------------------

/** Who chose an item's hour: the sales rule, measured results, or a person editing the plan. */
export const PLAN_HOUR_SOURCES = ["sales", "usual", "measured", "person"] as const;
export const PlanHourSource = z.enum(PLAN_HOUR_SOURCES);
export type PlanHourSource = z.infer<typeof PlanHourSource>;

export const WeekPlanItemDto = z.object({
  id: Uuid,
  day: LocalDate,
  hour: z.number().int(),
  hourSource: PlanHourSource,
  kind: PlanItemKind,
  channel: z.string(),
  surface: CreativeSurface,
  topicId: Uuid,
  topicCode: z.string(),
  topicTitle: z.string(),
  archetype: z.string().nullable(),
  varies: z.array(z.string()),
  variants: z.number().int(),
  slot: z.enum(PLAN_ITEM_SLOTS),
  why: z.array(ReasonDto),
});
export type WeekPlanItemDto = z.infer<typeof WeekPlanItemDto>;

export const WeekPlanDto = z.object({
  id: Uuid,
  weekStart: LocalDate,
  status: WeekPlanStatus,
  summary: z.string().nullable(),
  why: z.array(ReasonDto),
  warnings: z.array(z.string()),
  fromRules: z.boolean(),
  proposedAt: IsoDateTime,
  decidedAt: IsoDateTime.nullable(),
  decidedBy: z.string().nullable(),
  rejectReason: z.string().nullable(),
  handedOffAt: IsoDateTime.nullable(),
  handoffError: z.string().nullable(),
  items: z.array(WeekPlanItemDto),
});
export type WeekPlanDto = z.infer<typeof WeekPlanDto>;

export const WeekPlanListQuery = PageQuery.extend({ status: WeekPlanStatus.optional() });
export type WeekPlanListQuery = z.infer<typeof WeekPlanListQuery>;
export const ProposeWeekPlanRequest = z.object({ weekStart: LocalDate.optional(), replace: z.boolean().default(false) }).strict();
export type ProposeWeekPlanRequest = z.input<typeof ProposeWeekPlanRequest>;
export const RejectWeekPlanRequest = z.object({ reason: Note.optional() }).strict();

export const WeekPlanItemPatch = z
  .object({
    day: LocalDate,
    hour: z.number().int().min(0).max(23),
    channel: z.string().trim().min(1).max(40),
    surface: CreativeSurface,
    topicId: Uuid,
    varies: z.array(z.string().min(1).max(40)).max(6),
    variants: z.number().int().min(1).max(6),
  })
  .partial()
  .strict();
export type WeekPlanItemPatch = z.infer<typeof WeekPlanItemPatch>;

const PauseRange = z
  .object({ from: LocalDate, to: LocalDate, reason: z.string().trim().max(200).optional() })
  .refine((p) => p.from <= p.to, { path: ["to"], message: "to is before from" });

/** What a set of pauses would catch, asked before they are saved. */
export const PauseImpactRequest = z.object({ pauses: z.array(PauseRange).max(50) }).strict();
export type PauseImpactRequest = z.infer<typeof PauseImpactRequest>;
export const PauseImpactDto = z.object({ count: z.number().int().min(0) });
export type PauseImpactDto = z.infer<typeof PauseImpactDto>;

export const MAX_USUAL_HOURS = 6;
const UsualHours = z.object({
  channel: z.string().trim().min(1).max(40),
  hours: z
    .array(z.number().int().min(0).max(23))
    .min(1)
    .max(MAX_USUAL_HOURS)
    .transform((hours) => [...new Set(hours)].sort((a, b) => a - b)),
});

export const PlanSettingsDto = z.object({
  planDay: z.number().int(),
  planHour: z.number().int(),
  autoPropose: z.boolean(),
  postsPerWeek: z.number().int(),
  adsPerWeek: z.number().int(),
  emailsPerWeek: z.number().int(),
  roundEnabled: z.boolean(),
  roundCount: z.number().int(),
  competitorSync: z.boolean(),
  shortenLinks: z.boolean(),
  pauses: z.array(z.object({ from: LocalDate, to: LocalDate, reason: z.string().optional() })),
  /** The hours a post goes out on a channel until results measure better ones. */
  usualHours: z.array(z.object({ channel: z.string(), hours: z.array(z.number().int()) })),
  /** A token is stored. The token itself never leaves the service. */
  hasAdLibraryToken: z.boolean(),
});
export type PlanSettingsDto = z.infer<typeof PlanSettingsDto>;
export const PlanSettingsPut = z
  .object({
    planDay: z.number().int().min(1).max(7),
    planHour: z.number().int().min(0).max(23),
    autoPropose: z.boolean(),
    postsPerWeek: z.number().int().min(0).max(21),
    adsPerWeek: z.number().int().min(0).max(10),
    emailsPerWeek: z.number().int().min(0).max(7),
    roundEnabled: z.boolean(),
    roundCount: z.number().int().min(1).max(MAX_ROUND_TOPICS),
    competitorSync: z.boolean(),
    shortenLinks: z.boolean(),
    pauses: z.array(PauseRange).max(50),
    usualHours: z
      .array(UsualHours)
      .max(20)
      .refine((rows) => new Set(rows.map((row) => row.channel)).size === rows.length, { message: "a channel is listed twice" }),
    /** Null removes it. */
    adLibraryToken: z.string().trim().min(10).max(1_000).nullable(),
  })
  .partial()
  .strict();
export type PlanSettingsPut = z.input<typeof PlanSettingsPut>;

// ---------------------------------------------------------------------------
// Competitors
// ---------------------------------------------------------------------------

const PageId = z.string().regex(/^\d{1,32}$/u, "page ids are digits");

export const CompetitorDto = z.object({
  id: Uuid,
  name: z.string(),
  pageIds: z.array(z.string()),
  searchTerms: z.array(z.string()),
  country: z.string().nullable(),
  status: z.enum(["active", "archived"]),
  lastSyncAt: IsoDateTime.nullable(),
  lastError: z.string().nullable(),
  activeAds: z.number().int(),
});
export type CompetitorDto = z.infer<typeof CompetitorDto>;

const CompetitorFields = {
  name: z.string().trim().min(1).max(120),
  pageIds: z.array(PageId).max(MAX_COMPETITOR_PAGES),
  searchTerms: z.array(z.string().trim().min(2).max(100)).max(MAX_COMPETITOR_TERMS),
  country: z.string().regex(/^[A-Z]{2}$/u).nullable(),
};
export const CompetitorInput = z
  .object({ ...CompetitorFields, pageIds: CompetitorFields.pageIds.default([]), searchTerms: CompetitorFields.searchTerms.default([]), country: CompetitorFields.country.optional() })
  .strict();
export type CompetitorInput = z.input<typeof CompetitorInput>;
export const CompetitorPatch = z.object({ ...CompetitorFields, status: z.enum(["active", "archived"]) }).partial().strict();
export type CompetitorPatch = z.infer<typeof CompetitorPatch>;

export const CompetitorAdDto = z.object({
  id: Uuid,
  competitorId: Uuid,
  adArchiveId: z.string(),
  pageName: z.string().nullable(),
  deliveryStart: IsoDateTime.nullable(),
  deliveryStop: IsoDateTime.nullable(),
  active: z.boolean(),
  daysRunning: z.number().int(),
  bodies: z.array(z.string()),
  titles: z.array(z.string()),
  platforms: z.array(z.string()),
  /** The public Ad Library page, which never carries a token. */
  link: z.string(),
  ideaTopicId: Uuid.nullable(),
});
export type CompetitorAdDto = z.infer<typeof CompetitorAdDto>;
export const CompetitorAdsQuery = PageQuery.extend({ competitorId: Uuid.optional(), active: z.enum(["true", "false"]).optional() });
export type CompetitorAdsQuery = z.infer<typeof CompetitorAdsQuery>;
export const UseAdAsIdeaRequest = z.object({ title: Title.optional(), note: Note.optional() }).strict();
export const CompetitorSyncResult = z.object({
  competitorId: Uuid,
  added: z.number().int(),
  updated: z.number().int(),
  deactivated: z.number().int(),
  error: z.string().nullable(),
});
export type CompetitorSyncResult = z.infer<typeof CompetitorSyncResult>;

// ---------------------------------------------------------------------------
// Product news
// ---------------------------------------------------------------------------

export const ProductNewsDto = z.object({
  id: Uuid,
  title: z.string(),
  description: z.string().nullable(),
  date: LocalDate,
  link: z.string().nullable(),
  factIds: z.array(Uuid),
  source: z.enum(NEWS_SOURCES),
  /** The item it came from, when it came from one. */
  feedKey: z.string().nullable(),
  status: z.enum(NEWS_STATUSES),
  validUntil: IsoDateTime.nullable(),
  topicId: Uuid.nullable(),
});
export type ProductNewsDto = z.infer<typeof ProductNewsDto>;
export const ProductNewsInput = z
  .object({
    title: Title,
    description: LongText.nullable().optional(),
    date: LocalDate.optional(),
    link: z.url().max(500).nullable().optional(),
    factIds: z.array(Uuid).max(20).default([]),
    validUntil: IsoDateTime.nullable().optional(),
  })
  .strict();
export type ProductNewsInput = z.input<typeof ProductNewsInput>;
export const ProductNewsPatch = z
  .object({
    title: Title,
    description: LongText.nullable(),
    date: LocalDate,
    link: z.url().max(500).nullable(),
    factIds: z.array(Uuid).max(20),
    validUntil: IsoDateTime.nullable(),
    status: z.enum(NEWS_STATUSES),
  })
  .partial()
  .strict();
export type ProductNewsPatch = z.infer<typeof ProductNewsPatch>;
export const ProductNewsListQuery = PageQuery.extend({ status: z.enum(NEWS_STATUSES).optional() });
export type ProductNewsListQuery = z.infer<typeof ProductNewsListQuery>;
export const NewsSyncResponse = z.object({ added: z.number().int(), updated: z.number().int(), retired: z.number().int() });
export const NewsToTopicResponse = z.object({ news: ProductNewsDto, topic: TopicDto });

// ---------------------------------------------------------------------------
// Generation runs and the context a person can inspect
// ---------------------------------------------------------------------------

export const TopicRunDto = z.object({
  id: Uuid,
  kind: z.enum(TOPIC_RUN_KINDS),
  status: z.enum(TOPIC_RUN_STATUSES),
  requested: z.number().int(),
  produced: z.number().int(),
  repaired: z.number().int(),
  dropped: z.array(DroppedProposal),
  error: z.string().nullable(),
  startedAt: IsoDateTime,
  finishedAt: IsoDateTime.nullable(),
});
export type TopicRunDto = z.infer<typeof TopicRunDto>;

export const PlanningInputDto = z.object({ ref: z.string(), source: ReasonSource, label: z.string(), numbers: z.array(z.string()) });
export type PlanningInputDto = z.infer<typeof PlanningInputDto>;
export const PlanningContextQuery = z.object({ from: LocalDate.optional(), to: LocalDate.optional() });
export const PlanningContextDto = z.object({
  from: LocalDate,
  to: LocalDate,
  inputs: z.array(PlanningInputDto),
});
